/**
 * ClosingCallScreen — the post-delivery closing call (mockup #s-closing_call).
 *
 * Completion is an explicit stored boolean today (`closingCallDone` on the
 * project doc), written through the existing `updateProject` project-patch path
 * (the key is whitelisted in projectStore.projectPatchBody). It is NOT a gate
 * field — no gateState write, no rules change.
 *
 * EXPLICIT NOW / AUTO LATER: the manual "Mark closing call done" toggle is the
 * source today. The L1A_STAGE_CONFIG.autoDerive seam flips closing_call to
 * task-driven later (done when a scheduled closing-call task is logged) — the
 * derivation already reads that future signal; only the writer changes.
 */
import { useState } from 'react';
import { Box, Btn3, Tag } from '../ui';
import { updateProject } from '../../lib/projectStore';

export default function ClosingCallScreen({
  pid,
  done,
  delivered,
  mayCommit,
  actorName,
  onMsg,
}: {
  pid: string;
  done: boolean;
  /** the report has been delivered — the call can only happen after delivery. */
  delivered: boolean;
  mayCommit: boolean;
  actorName: string;
  onMsg: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function setDone(next: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      await updateProject(pid, { closingCallDone: next }, actorName);
      onMsg(next ? 'Closing call marked done.' : 'Closing call reopened.');
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box title="Closing call" note="Walk the client through the recommendation — after delivery">
      <div className="l1a-screen">
        <div className="kv">
          <span className="k">Call status</span>
          {done ? <Tag kind="val">done</Tag> : <Tag>not done</Tag>}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        {!done ? (
          <Btn3
            intent="grn"
            size="sm"
            disabled={busy || !mayCommit || !delivered}
            title={
              !delivered
                ? 'Available after the report is delivered'
                : !mayCommit
                  ? 'Only the project assignee or an admin can mark this'
                  : 'Mark the closing call complete'
            }
            onClick={() => setDone(true)}
          >
            {busy ? 'Saving…' : 'Mark closing call done'}
          </Btn3>
        ) : (
          <Btn3
            intent="ghost"
            size="sm"
            disabled={busy || !mayCommit}
            onClick={() => setDone(false)}
          >
            Reopen (mark not done)
          </Btn3>
        )}
      </div>

      <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '12px 0 0', lineHeight: 1.55 }}>
        Manual mark today; designed to flip to automatic later (e.g. when a
        scheduled closing-call task is logged).
      </p>
    </Box>
  );
}
